import React, { useState } from 'react';
import './PrintTicketModal.css';

interface ViolationDetail {
    violationTrackerId: string;
    plateNumber: string;
    lotId: string;
    vehicleId: string;
    violationType: string;
    violationTimestamp: string;
    vehicleMake: string;
    vehicleModel: string;
    vehicleColor: string;
    totalViolationAmount: number | null;
    violationStatus: string;
    evidenceURL: string;
    recordedBy: string;
    recordedOn: string;
    initialNotes: string;
    resolvedBy: string;
    resolvedOn: string;
    resolutionNotes: string;
    dismissedBy: string;
    dismissedOn: string;
    dismissalNotes: string;
    disputedBy: string;
    disputedOn: string;
    disputeNotes: string;
}

interface LotData {
    lotId: string;
    lotName: string;
    companyName: string;
    address: string;
    lotCapacity: number;
    registryOn: boolean;
    ownerCustomerId: string;
    accountStatus: string;
    isDeleted: boolean;
    [key: string]: any;
}

interface PrintTicketModalProps {
    isOpen: boolean;
    onClose: () => void;
    violationDetails: ViolationDetail[];
    lotData: LotData | null;
    isSummaryTicket?: boolean;
}

const PrintTicketModal: React.FC<PrintTicketModalProps> = ({
    isOpen,
    onClose,
    violationDetails,
    lotData,
    isSummaryTicket = false
}) => {
    const minWidth = 50;
    const maxWidth = 80;

    // Default values for comparison
    const defaultValues = {
        logo: '',
        terms: 'This parking violation is due within 14 days. Failure to pay may result in additional fees, vehicle immobilization, or towing. For questions regarding this violation, please contact the parking office.',
        address: lotData?.address || 'Contact parking office for address'
    };

    // Load saved values from localStorage
    const loadSavedValues = () => {
        try {
            const savedLogo = localStorage.getItem('parkingTicket_logo') || '';
            const savedTerms = localStorage.getItem('parkingTicket_terms') || defaultValues.terms;
            const savedAddress = localStorage.getItem('parkingTicket_address') || defaultValues.address;
            const savedWidth = localStorage.getItem('parkingTicket_width');
            return {
                logo: savedLogo,
                terms: savedTerms,
                address: savedAddress,
                width: savedWidth ? parseInt(savedWidth) : 57
            };
        } catch (error) {
            console.error('Error loading saved values:', error);
            return { ...defaultValues, width: 57 };
        }
    };

    const savedValues = loadSavedValues();

    const [ticketWidth, setTicketWidth] = useState(savedValues.width); // Use saved width
    const [logoUrl, setLogoUrl] = useState<string>(savedValues.logo);
    const [isLogoModalOpen, setIsLogoModalOpen] = useState(false);
    const [isRemoveLogoModalOpen, setIsRemoveLogoModalOpen] = useState(false);
    const [logoInputUrl, setLogoInputUrl] = useState('');
    const [logoError, setLogoError] = useState('');

    // Helper functions
    const getViolationTypeDisplay = (type: string) => {
        switch (type) {
            case 'OVERSTAY': return 'Overstay';
            case 'UNPAID_SESSION': return 'Unpaid';
            case 'NO_PERMIT': return 'No Permit';
            default: return type;
        }
    };

    const totalAmount = violationDetails.reduce((sum, violation) => sum + (violation.totalViolationAmount || 0), 0);
    const firstViolation = violationDetails[0];

    // Editable text state with saved values
    const [editableText, setEditableText] = useState({
        type: 'Private Property - ' + (lotData?.lotName || 'Parking Lot'),
        region: 'Ontario',
        offence: isSummaryTicket ? 'Multiple Violations' : (firstViolation ? getViolationTypeDisplay(firstViolation.violationType) : 'N/A'),
        location: lotData?.lotName || 'N/A',
        notes: firstViolation?.initialNotes || '',
        terms: savedValues.terms,
        address: savedValues.address
    });

    // Edit modal state
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [editingField, setEditingField] = useState('');
    const [editValue, setEditValue] = useState('');

    // Check if any values differ from defaults
    const hasCustomValues = () => {
        return logoUrl !== defaultValues.logo ||
            editableText.terms !== defaultValues.terms ||
            editableText.address !== defaultValues.address;
    };

    // Save values to localStorage
    const saveToStorage = (key: string, value: string) => {
        try {
            localStorage.setItem(`parkingTicket_${key}`, value);
        } catch (error) {
            console.error('Error saving to localStorage:', error);
        }
    };

    // Reset all values to defaults
    const handleReset = () => {
        setLogoUrl(defaultValues.logo);
        setEditableText(prev => ({
            ...prev,
            terms: defaultValues.terms,
            address: defaultValues.address
        }));

        // Clear from localStorage
        localStorage.removeItem('parkingTicket_logo');
        localStorage.removeItem('parkingTicket_terms');
        localStorage.removeItem('parkingTicket_address');
    };

    // Calculate blue bar (fill) position and width as a percentage of the track
    // Ensure the blue bar exactly matches the ticket width
    const fillWidthPercent = (ticketWidth / maxWidth) * 100 // Slightly reduce to match ticket exactly
    const fillLeftPercent = (100 - fillWidthPercent) / 2; // Center the fill
    const leftHandlePercent = fillLeftPercent;
    const rightHandlePercent = fillLeftPercent + fillWidthPercent;

    if (!isOpen) return null;

    const formatCurrency = (amount: number | null) => {
        if (amount === null || amount === undefined) {
            return '$0.00';
        }
        return `$${amount.toFixed(2)}`;
    };

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            month: '2-digit',
            day: '2-digit',
            year: 'numeric'
        });
    };

    const formatTime = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
        });
    };

    const handleAddLogo = () => {
        setLogoInputUrl('');
        setLogoError('');
        setIsLogoModalOpen(true);
    };

    const handleLogoSubmit = () => {
        if (!logoInputUrl.trim()) {
            setLogoError('Please enter a logo URL');
            return;
        }

        // Basic URL validation
        try {
            new URL(logoInputUrl);
        } catch {
            setLogoError('Please enter a valid URL');
            return;
        }

        // Check if it's an image file
        const validExtensions = ['.svg', '.png', '.jpg', '.jpeg', '.gif'];
        const hasValidExtension = validExtensions.some(ext =>
            logoInputUrl.toLowerCase().includes(ext)
        );

        if (!hasValidExtension) {
            setLogoError('Please enter a URL to an image file (SVG, PNG, JPEG, GIF)');
            return;
        }

        setLogoUrl(logoInputUrl);
        saveToStorage('logo', logoInputUrl);
        setIsLogoModalOpen(false);
        setLogoInputUrl('');
        setLogoError('');
    };

    const handleRemoveLogo = () => {
        setIsRemoveLogoModalOpen(true);
    };

    const handleConfirmRemoveLogo = () => {
        setLogoUrl('');
        saveToStorage('logo', '');
        setIsRemoveLogoModalOpen(false);
    };

    const handleEditText = (field: string, currentValue: string) => {
        setEditingField(field);
        setEditValue(currentValue);
        setIsEditModalOpen(true);
    };

    const handleSaveEdit = () => {
        setEditableText(prev => ({
            ...prev,
            [editingField]: editValue
        }));

        // Save to localStorage if it's a persistent field
        if (editingField === 'terms' || editingField === 'address') {
            saveToStorage(editingField, editValue);
        }

        setIsEditModalOpen(false);
        setEditingField('');
        setEditValue('');
    };

    const getFieldDisplayName = (field: string) => {
        switch (field) {
            case 'type': return 'Type';
            case 'region': return 'Region';
            case 'offence': return 'Offence';
            case 'location': return 'Location';
            case 'notes': return 'Notes/Reason';
            case 'terms': return 'Terms & Conditions';
            case 'address': return 'Address';
            default: return field;
        }
    };

    const handleWidthChange = (newWidth: number) => {
        setTicketWidth(newWidth);
        saveToStorage('width', newWidth.toString());
    };

    // Escape HTML entities to prevent XSS when inserting into print window
    const escapeHtml = (unsafe: string): string => {
        return unsafe
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/"/g, "&quot;")
            .replace(/'/g, "&#039;");
    };

    // Create safe text content for print by cloning and escaping user-controlled fields
    const createSafePrintContent = (element: HTMLElement): string => {
        const clone = element.cloneNode(true) as HTMLElement;

        // The content is rendered from React state which is already controlled,
        // but we ensure any text that could have user input is properly escaped
        // by using textContent which automatically escapes HTML
        const textElements = clone.querySelectorAll('.ticket-number, .violation-notes, .notes-content');
        textElements.forEach((el) => {
            if (el.textContent) {
                el.textContent = el.textContent;
            }
        });

        return clone.innerHTML;
    };

    const handlePrint = () => {
        const printContent = document.getElementById('print-ticket-content');
        if (!printContent) {
            console.error('Print content element not found');
            return;
        }

        const printWindow = window.open('', '_blank');
        if (!printWindow) {
            console.error('Failed to open print window - popup may be blocked');
            alert('Unable to open print window. Please allow popups for this site and try again.');
            return;
        }

        const safePrintContent = createSafePrintContent(printContent);

        printWindow.document.write(`
                    <!DOCTYPE html>
                    <html>
                    <head>
                        <title>Parking Ticket</title>
                        <meta name="viewport" content="width=device-width, initial-scale=1.0">
                        <style>
                            body { 
                                font-family: Arial, sans-serif; 
                                margin: 0; 
                                padding: 20px; 
                                background-color: #ffffff;
                            }
                            .print-header {
                                display: flex;
                                justify-content: space-between;
                                align-items: center;
                                margin-bottom: 20px;
                                padding: 10px;
                                background-color: #fff;
                                border-radius: 8px;
                            }
                            .print-title {
                                font-size: 18px;
                                font-weight: bold;
                                color: #333;
                            }
                            .print-actions {
                                display: flex;
                                gap: 10px;
                            }
                            .print-btn, .close-btn {
                                padding: 8px 16px;
                                border: none;
                                border-radius: 20px;
                                cursor: pointer;
                                font-size: 14px;
                                font-weight: 500;
                                min-width: 100px;
                            }
                            .print-btn {
                                background-color: #007bff;
                                color: white;
                            }
                            .print-btn:hover {
                                background-color: #0056b3;
                            }
                            .close-btn {
                                background-color: #6c757d;
                                color: white;
                            }
                            .close-btn:hover {
                                background-color: #545b62;
                            }
                            .ticket { 
                                width: ${ticketWidth}mm; 
                                max-width: ${ticketWidth}mm; 
                                margin: 0 auto; 
                                border: 1px solid #000; 
                                padding: 10px; 
                                background-color: white;
                                box-shadow: 0 4px 8px rgba(0,0,0,0.1);
                            }
                            .ticket-header { text-align: center; margin-bottom: 15px; }
                            .violation-title { background-color: #000 !important; color: white !important; font-size: 12px; font-weight: bold; text-transform: uppercase; padding: 8px; margin-bottom: 10px; border-radius: 4px; -webkit-print-color-adjust: exact; color-adjust: exact; }
                            .lot-name-ticket { font-size: 14px; font-weight: bold; margin-bottom: 5px; }
                            .company-name { font-size: 12px; font-weight: bold; margin-bottom: 5px; }
                            .separator { border-top: 2px dashed #000; margin: 15px 0; height: 0; }
                            .info-row { display: flex; justify-content: flex-start; margin-bottom: 5px; font-size: 10px; }
                            .info-row .info-label { font-weight: bold; width: 70px; flex-shrink: 0; margin-right: 8px; font-size: 10px; }
                            .info-row span:not(.info-label) { text-align: left; flex: 1; min-width: 0; }
                            .amount { font-size: 20px; font-weight: bold; text-align: right; margin: 10px 0; }
                            .notes-section { margin: 10px 0; padding: 8px; border-left: 2px solid black; }
                            .notes-content { font-size: 10px; margin-top: 4px; white-space: pre-wrap; word-wrap: break-word; }
                            .individual-violation { margin: 8px 0; padding: 6px; border-left: 2px solid black; }
                            .individual-violation .info-row { margin-bottom: 3px; font-size: 9px; }
                            .individual-violation .info-row:last-child { margin-bottom: 0; }
                            .violation-notes { font-style: italic; color: #666; max-width: 60%; word-wrap: break-word; overflow-wrap: break-word; font-size: 9px; }
                            .terms { font-size: 9px; margin-top: 15px; line-height: 1.3; }
                            .contact { font-size: 9px; margin-top: 15px; text-align: center; }
                            .contact-title { font-weight: bold; margin-bottom: 5px; }
                            .ticket-number { font-size: 9px; font-family: monospace; word-wrap: break-word; overflow-wrap: break-word; }
                            .logo-section { text-align: center; margin-bottom: 10px; display: flex; justify-content: center; align-items: center; }
                            .logo-placeholder { border: 2px dashed #ccc; padding: 10px; margin: 5px 0; color: #666; font-size: 10px; cursor: pointer; }
                            .logo-image { width: 100%; max-height: 40px; object-fit: contain; cursor: pointer; }
                            .editable-text { cursor: pointer; transition: color 0.2s ease; }
                            .editable-text:hover { color: #007bff; }
                            @media print {
                                body { margin: 0; background-color: white; }
                                .print-header { display: none; }
                                .ticket { border: none; box-shadow: none; }
                                .violation-title { background-color: #000 !important; color: white !important; -webkit-print-color-adjust: exact; color-adjust: exact; }
                                .logo-placeholder { display: none; }
                                .editable-text { cursor: default; }
                                .editable-text:hover { color: inherit; }
                            }
                            @media (max-width: 768px) {
                                body { padding: 10px; }
                                .print-header { flex-direction: column; gap: 10px; }
                                .print-actions { width: 100%; justify-content: center; }
                                .ticket { width: 100%; max-width: 100%; }
                            }
                        </style>
                    </head>
                    <body>
                        <div class="print-header">
                            <div class="print-title">Parking Ticket Print Preview</div>
                            <div class="print-actions">
                                <button class="print-btn" onclick="window.print()">Print</button>
                                <button class="close-btn" onclick="window.close()">Close Tab</button>
                            </div>
                        </div>
                        ${safePrintContent}
                        <script>
                            // Auto-open print dialog when page loads
                            window.onload = function() {
                                setTimeout(function() {
                                    window.print();
                                }, 100);
                            };
                        </script>
                    </body>
                    </html>
                `);
        printWindow.document.close();
        printWindow.focus();

        // Close the modal after opening the print window
        onClose();
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal" onClick={(e) => e.stopPropagation()}>
                <div className="modal-close" onClick={onClose}>
                    <span className="modal-cancel-text">Cancel (ESC)</span>
                    <img src="/assets/x.svg" alt="Close" className="modal-close-icon" />
                </div>

                <h2>Parking Ticket Print Preview</h2>

                <div className="modal-content">
                    <div id="print-ticket-content" className="ticket-preview">
                        <div className="ticket" style={{ width: `${ticketWidth}mm` }}>
                            {/* Logo Section - Preview Only */}
                            {!logoUrl ? (
                                <div className="logo-section">
                                    <div className="logo-placeholder" onClick={handleAddLogo}>
                                        Add Logo
                                    </div>
                                </div>
                            ) : (
                                <div className="logo-section">
                                    <img
                                        src={logoUrl}
                                        alt="Company Logo"
                                        className="logo-image"
                                        onClick={handleRemoveLogo}
                                        onError={() => {
                                            setLogoError('Failed to load image');
                                            setLogoUrl('');
                                        }}
                                    />
                                </div>
                            )}

                            <div className="ticket-header">
                                <div className="violation-title">PARKING VIOLATION</div>
                                <div className="lot-name-ticket">{lotData?.lotName || 'Parking Lot'}</div>
                                <div className="company-name">{lotData?.companyName || 'Parallel Parking'}</div>
                            </div>

                            <div className="separator"></div>

                            <div className="info-row">
                                <span className="info-label">No.:</span>
                                <span className="ticket-number">{firstViolation?.violationTrackerId || 'N/A'}</span>
                            </div>

                            <div className="info-row">
                                <span className="info-label">Date:</span>
                                <span>{firstViolation ? `${formatDate(firstViolation.violationTimestamp)} ${formatTime(firstViolation.violationTimestamp)}` : 'N/A'}</span>
                            </div>

                            <div className="info-row">
                                <span className="info-label">Type:</span>
                                <span
                                    className="editable-text"
                                    onClick={() => handleEditText('type', editableText.type)}
                                >
                                    {editableText.type}
                                </span>
                            </div>

                            <div className="info-row">
                                <span className="info-label">Plate:</span>
                                <span>{firstViolation?.plateNumber || 'N/A'}</span>
                            </div>

                            <div className="info-row">
                                <span className="info-label">Region:</span>
                                <span
                                    className="editable-text"
                                    onClick={() => handleEditText('region', editableText.region)}
                                >
                                    {editableText.region}
                                </span>
                            </div>

                            {isSummaryTicket ? (
                                <>
                                    <div className="info-row">
                                        <span className="info-label">Offence:</span>
                                        <span
                                            className="editable-text"
                                            onClick={() => handleEditText('offence', editableText.offence)}
                                        >
                                            {editableText.offence}
                                        </span>
                                    </div>
                                    <div className="info-row">
                                        <span className="info-label">Location:</span>
                                        <span
                                            className="editable-text"
                                            onClick={() => handleEditText('location', editableText.location)}
                                        >
                                            {editableText.location}
                                        </span>
                                    </div>
                                    <div className="info-row">
                                        <span className="info-label">Writer:</span>
                                        <span>{firstViolation?.recordedBy || 'N/A'}</span>
                                    </div>

                                    {/* Individual violation details */}
                                    {violationDetails.map((violation, index) => (
                                        <div key={violation.violationTrackerId} className="individual-violation">
                                            <div className="info-row">
                                                <span className="info-label">Offence {index + 1}:</span>
                                                <span>{getViolationTypeDisplay(violation.violationType)}</span>
                                            </div>
                                            <div className="info-row">
                                                <span className="info-label">Amount {index + 1}:</span>
                                                <span>{formatCurrency(violation.totalViolationAmount)}</span>
                                            </div>
                                            {violation.initialNotes && (
                                                <div className="info-row">
                                                    <span className="info-label">Reason {index + 1}:</span>
                                                    <span className="violation-notes">{violation.initialNotes}</span>
                                                </div>
                                            )}
                                        </div>
                                    ))}

                                    <div className="amount">
                                        Total: {formatCurrency(totalAmount)}
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div className="info-row">
                                        <span className="info-label">Offence:</span>
                                        <span
                                            className="editable-text"
                                            onClick={() => handleEditText('offence', editableText.offence)}
                                        >
                                            {editableText.offence}
                                        </span>
                                    </div>
                                    <div className="info-row">
                                        <span className="info-label">Location:</span>
                                        <span
                                            className="editable-text"
                                            onClick={() => handleEditText('location', editableText.location)}
                                        >
                                            {editableText.location}
                                        </span>
                                    </div>
                                    <div className="info-row">
                                        <span className="info-label">Writer:</span>
                                        <span>{firstViolation?.recordedBy || 'N/A'}</span>
                                    </div>
                                    <div className="amount">
                                        {formatCurrency(firstViolation?.totalViolationAmount || 0)}
                                    </div>
                                </>
                            )}

                            {!isSummaryTicket && firstViolation?.initialNotes && (
                                <div className="notes-section">
                                    <div className="info-label">Reason:</div>
                                    <div
                                        className="notes-content editable-text"
                                        onClick={() => handleEditText('notes', editableText.notes)}
                                    >
                                        {editableText.notes}
                                    </div>
                                </div>
                            )}

                            <div
                                className="terms editable-text"
                                onClick={() => handleEditText('terms', editableText.terms)}
                            >
                                {editableText.terms}
                            </div>

                            <div className="contact">
                                <div className="contact-title">{lotData?.companyName || 'Parallel Parking'}</div>
                                <div
                                    className="editable-text"
                                    onClick={() => handleEditText('address', editableText.address)}
                                >
                                    {editableText.address}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="width-slider-container">
                        <div className="width-slider">
                            <div className="width-slider-track">
                                <div
                                    className="width-slider-fill"
                                    style={{
                                        left: `${fillLeftPercent}%`,
                                        width: `${fillWidthPercent}%`
                                    }}
                                ></div>
                                <div
                                    className="width-slider-handle left-handle"
                                    style={{
                                        left: `${leftHandlePercent}%`
                                    }}
                                ></div>
                                <div
                                    className="width-slider-handle right-handle"
                                    style={{
                                        left: `${rightHandlePercent}%`
                                    }}
                                ></div>
                                <input
                                    type="range"
                                    min={minWidth}
                                    max={maxWidth}
                                    value={ticketWidth}
                                    onChange={(e) => handleWidthChange(parseInt(e.target.value))}
                                    className="width-slider-input"
                                />
                            </div>
                            <div className="width-slider-label">{ticketWidth}mm</div>
                        </div>
                    </div>
                </div>

                <div className="modal-actions">
                    <button className="button-primary" onClick={handlePrint}>
                        Print
                    </button>
                    {hasCustomValues() ? (
                        <button className="button-secondary" onClick={handleReset}>
                            Reset
                        </button>
                    ) : (
                        <button className="button-secondary" onClick={onClose}>
                            Cancel
                        </button>
                    )}
                </div>
            </div>

            {/* Logo URL Input Modal */}
            {isLogoModalOpen && (
                <div className="modal-overlay" onClick={() => setIsLogoModalOpen(false)}>
                    <div className="modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-close" onClick={() => setIsLogoModalOpen(false)}>
                            <span className="modal-cancel-text">Cancel</span>
                            <img src="/assets/x.svg" alt="Close" className="modal-close-icon" />
                        </div>

                        <h2>Add Logo</h2>
                        <div className="modal-content">
                            <p>Paste link to logo, must be an SVG, JPEG, PNG, or GIF:</p>
                            <input
                                type="text"
                                value={logoInputUrl}
                                onChange={(e) => setLogoInputUrl(e.target.value)}
                                placeholder="https://example.com/logo.png"
                                className="logo-url-input"
                            />
                            {logoError && <div className="error-message">{logoError}</div>}
                        </div>

                        <div className="modal-actions">
                            <button className="button-primary" onClick={handleLogoSubmit}>
                                Add Logo
                            </button>
                            <button className="button-secondary" onClick={() => setIsLogoModalOpen(false)}>
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Remove Logo Confirmation Modal */}
            {isRemoveLogoModalOpen && (
                <div className="modal-overlay" onClick={() => setIsRemoveLogoModalOpen(false)}>
                    <div className="modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-close" onClick={() => setIsRemoveLogoModalOpen(false)}>
                            <span className="modal-cancel-text">Cancel</span>
                            <img src="/assets/x.svg" alt="Close" className="modal-close-icon" />
                        </div>

                        <h2>Remove Logo?</h2>
                        <div className="modal-content">
                            <p>Are you sure you want to remove the logo from the ticket?</p>
                        </div>

                        <div className="modal-actions">
                            <button className="button-primary" onClick={handleConfirmRemoveLogo}>
                                Remove
                            </button>
                            <button className="button-secondary" onClick={() => setIsRemoveLogoModalOpen(false)}>
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Text Modal */}
            {isEditModalOpen && (
                <div className="modal-overlay" onClick={() => setIsEditModalOpen(false)}>
                    <div className="modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-close" onClick={() => setIsEditModalOpen(false)}>
                            <span className="modal-cancel-text">Cancel</span>
                            <img src="/assets/x.svg" alt="Close" className="modal-close-icon" />
                        </div>

                        <h2>Edit {getFieldDisplayName(editingField)}</h2>
                        <div className="modal-content">
                            <p>Current value: {editableText[editingField as keyof typeof editableText]}</p>
                            <input
                                type="text"
                                value={editValue}
                                onChange={(e) => setEditValue(e.target.value)}
                                className="edit-input"
                            />
                        </div>

                        <div className="modal-actions">
                            <button className="button-primary" onClick={handleSaveEdit}>
                                Save
                            </button>
                            <button className="button-secondary" onClick={() => setIsEditModalOpen(false)}>
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PrintTicketModal; 